import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Message, ChatSession, ChatState, Prompt } from '../../types/chat';
import ChatBubble from './ChatBubble';
import ChatWindow from './ChatWindow';

// ============================================================================
// Component
// ============================================================================
const Chat: React.FC = () => {
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [availablePrompts, setAvailablePrompts] = useState<Prompt[]>([]);
  const [chatState, setChatState] = useState<ChatState>({
    messages: [],
    isTyping: false,
    isOpen: false,
    error: null,
    selectedPrompt: null
  });

  // Load available prompts when component mounts
  useEffect(() => {
    loadPrompts();
  }, []);

  // Load messages when session changes
  useEffect(() => {
    if (currentSession) {
      loadMessages();
    }
  }, [currentSession]);

  const loadPrompts = async () => {
    try {
      const response = await api.get('/chat/prompts');
      setAvailablePrompts(response.data);
      
      // Set default prompt if none selected
      if (!chatState.selectedPrompt && response.data.length > 0) {
        const defaultPrompt = response.data.find((p: Prompt) => p.action_type === 'chat_general') || response.data[0];
        setChatState(prev => ({
          ...prev,
          selectedPrompt: defaultPrompt
        }));
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
    }
  };

  const loadMessages = async () => {
    if (!currentSession) return;
    
    try {
      const response = await api.get(`/chat/sessions/${currentSession.id}/messages`);
      setChatState(prev => ({
        ...prev,
        messages: response.data,
        error: null
      }));
    } catch (error) {
      setChatState(prev => ({
        ...prev,
        error: 'Failed to load messages'
      }));
    }
  };

  const handlePromptChange = (prompt: Prompt) => {
    setChatState(prev => ({
      ...prev,
      selectedPrompt: prompt
    }));
  };

  const handleSendMessage = async (message: string) => {
    let sessionToUse = currentSession;
    
    // Create session if none exists (first message)
    if (!sessionToUse) {
      try {
        const response = await api.post('/chat/sessions');
        sessionToUse = response.data;
        setCurrentSession(response.data);
      } catch (error) {
        setChatState(prev => ({
          ...prev,
          error: 'Failed to create session'
        }));
        return;
      }
    }

    // Session should exist at this point
    if (!sessionToUse) {
      setChatState(prev => ({
        ...prev,
        error: 'No session available'
      }));
      return;
    }

    const userMessage: Message = {
      id: Date.now(),
      role: 'user',
      content: message,
      created_at: new Date().toISOString()
    };

    setChatState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isTyping: true,
      error: null
    }));

    try {
      // If it's a database assistant, use MCP endpoints directly
      if (chatState.selectedPrompt?.action_type === 'chat_database') {
        const response = await api.post('/mcp/ask', {
          prompt: message
        });

        const assistantMessage: Message = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.message || response.data.error || 'No response received',
          created_at: new Date().toISOString()
        };

        setChatState(prev => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          isTyping: false
        }));
      } else {
        // Regular chat
        const response = await api.post('/chat', {
          message,
          session_id: sessionToUse.id,
          prompt_id: chatState.selectedPrompt?.id
        });

        const assistantMessage: Message = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.message,
          created_at: new Date().toISOString()
        };

        setChatState(prev => ({
          ...prev,
          messages: [...prev.messages, assistantMessage],
          isTyping: false
        }));
      }
    } catch (error) {
      setChatState(prev => ({
        ...prev,
        isTyping: false,
        error: 'Failed to send message'
      }));
    }
  };

  const startNewSession = async () => {
    try {
      const response = await api.post('/chat/sessions');
      setCurrentSession(response.data);
    } catch (error) {
      setChatState(prev => ({
        ...prev,
        error: 'Failed to start new session'
      }));
    }
  };

  const completeSession = async (sessionId: number, messages: Message[]) => {
    try {
      if (messages.length === 0) {
        // No messages to summarize, just mark as completed
        await api.put(`/chat/sessions/${sessionId}`, {
          status: 'completed'
        });
        return;
      }

      // Format conversation context for AI summary
      const conversationContext = messages
        .map(msg => `${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`)
        .join('\n\n');

      // Call backend to complete session with context for AI summary
      await api.put(`/chat/sessions/${sessionId}`, {
        status: 'completed',
        context: conversationContext
      });
    } catch (error) {
      console.error('Failed to complete session:', error);
      // Don't show error to user since this is cleanup
    }
  };

  const toggleChat = () => {
    setChatState(prev => {
      const newIsOpen = !prev.isOpen;
      
      // If closing the chat, complete the current session and clear local state
      if (!newIsOpen && currentSession && prev.messages.length > 0) {
        completeSession(currentSession.id, prev.messages);
      }
      
      if (!newIsOpen) {
        setCurrentSession(null);
        return {
          ...prev,
          isOpen: newIsOpen,
          messages: [],
          error: null
        };
      } else {
        // Opening the chat - don't create session until first message
        return {
          ...prev,
          isOpen: newIsOpen
        };
      }
    });
  };

  return (
    <>
      <ChatBubble onClick={toggleChat} isOpen={chatState.isOpen} />
      <ChatWindow
        isOpen={chatState.isOpen}
        onClose={toggleChat}
        messages={chatState.messages}
        onSendMessage={handleSendMessage}
        isTyping={chatState.isTyping}
        error={chatState.error}
        selectedPrompt={chatState.selectedPrompt}
        availablePrompts={availablePrompts}
        onPromptChange={handlePromptChange}
      />
    </>
  );
};

export default Chat;